import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useOrgAuthInfo } from '@/hooks/useOrgAuthInfo';
import isSlackConfigured from '@/apis/isSlackConfigured';
import { manageCursorUserMappings } from '@/apis/cursorIntegration';
import { manageClaudeCodeUserMappings } from '@/apis/claudeCodeIntegration';
import type { IntegrationName } from '@/types/IntegrationsType';

function apiNameToLocal(apiName: string): IntegrationName | null {
  const n = apiName.toLowerCase();
  const map: Record<string, IntegrationName> = {
    sentry: 'Sentry',
    better_stack: 'BetterStack',
    betterstack: 'BetterStack',
    pagerduty: 'PagerDuty',
    datadog: 'Datadog',
    new_relic: 'NewRelic',
    newrelic: 'NewRelic',
    incident_io: 'Incident.io',
    claude: 'Claude',
    cursor: 'Cursor',
    slack: 'Slack',
    slackbot: 'Slack',
    discord: 'Discord',
    linear: 'Linear',
    jira: 'Jira',
    asana: 'Asana',
    intercom: 'Intercom',
    pylon: 'Pylon',
    zendesk: 'Zendesk',
    sumologic: 'SumoLogic',
  };
  return map[n] ?? null;
}

export function useIntegrationStatus() {
  const { user, organization } = useUserLogged();
  const orgUUID = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const queryClient = useQueryClient();

  // Integration rows come from the shared /fetchAuthInfo/ query, so this hook
  // reuses the same cache entry as every other consumer instead of refetching.
  const { integrations, isLoading: authInfoLoading } = useOrgAuthInfo();

  // Slack, Cursor and Claude are not represented in the auth-info rows, so they
  // need their own probes. They live in a separate query so consumers that only
  // want auth-info do not pay for these three extra requests.
  const probes = useQuery({
    queryKey: ['integrationStatus', orgUUID],
    queryFn: async () => {
      if (!orgUUID || !user) return null;

      const headers = {
        Orguuid: orgUUID,
        Useruuid: user.UserUUID ?? '',
        Useremail: user.Email ?? user.email ?? '',
        Username: user.FirstName ?? '',
        Orgname: organization?.OrgName ?? '',
      };

      const [slackRes, cursorRes, claudeRes] = await Promise.allSettled([
        user.UserUUID
          ? isSlackConfigured(
              { OrgUUID: orgUUID },
              {
                Useremail: user.Email ?? user.email ?? '',
                Useruuid: user.UserUUID ?? '',
                Orgname: organization?.OrgName ?? '',
                Orguuid: orgUUID,
              }
            )
          : Promise.resolve(false),
        manageCursorUserMappings(
          { org_uuid: orgUUID, action: 'list' },
          headers
        ),
        manageClaudeCodeUserMappings(
          { org_uuid: orgUUID, action: 'list' },
          headers
        ),
      ]);

      return {
        slack: slackRes.status === 'fulfilled' && !!slackRes.value,
        cursor:
          cursorRes.status === 'fulfilled' &&
          !!cursorRes.value?.cursor_configured,
        claude: claudeRes.status === 'fulfilled' ? claudeRes.value : null,
      };
    },
    enabled: !!orgUUID,
    staleTime: 30 * 1000,
  });

  const connected = useMemo(() => {
    const next = new Set<IntegrationName>();

    integrations.forEach((i) => {
      const local = apiNameToLocal(i.IntegrationName ?? '');
      if (local) next.add(local);
    });

    const probeData = probes.data;
    if (probeData) {
      if (probeData.slack) next.add('Slack');
      if (probeData.cursor) next.add('Cursor');
      if (probeData.claude) {
        // Prefer explicit analytics_ready; fall back to admin_key_configured;
        // legacy fallback: claude_configured when backend hasn't shipped dual keys.
        const res = probeData.claude;
        const analyticsReady =
          res?.analytics_ready ?? res?.admin_key_configured ?? undefined;
        if (
          analyticsReady === true ||
          (analyticsReady === undefined && res?.claude_configured)
        ) {
          next.add('Claude');
        }
      }
    }

    return next;
  }, [integrations, probes.data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['orgAuthInfo', orgUUID] });
    queryClient.invalidateQueries({
      queryKey: ['integrationStatus', orgUUID],
    });
  };

  return {
    connected,
    isLoading: authInfoLoading || probes.isLoading,
    refresh,
  };
}
